import { Component } from '@theme/component';

/**
 * Suggested reading-add power by age band (not mandatory — the patient can
 * still type any value in the Add fields). Ranges per the optical guidance
 * this store follows.
 */
const ADD_BY_AGE = [
  { min: 39, max: 40, add: 1.0 },
  { min: 41, max: 42, add: 1.25 },
  { min: 43, max: 45, add: 1.5 },
  { min: 46, max: 48, add: 1.75 },
  { min: 49, max: 50, add: 2.0 },
  { min: 51, max: 52, add: 2.25 },
  { min: 53, max: 55, add: 2.5 },
  { min: 56, max: 58, add: 2.75 },
  { min: 59, max: Infinity, add: 3.0 },
];

/**
 * Thickness guidance by prescription strength (largest absolute SPH/CYL, either
 * eye). Reduction is offered above 2.00 and recommended up to 4.00; high-index
 * starts at 4.25 (the next 0.25 step above 4.00).
 */
const REDUCTION_MIN_POWER = 2;
const REDUCTION_MAX_RECOMMENDED = 4;
const HIGH_INDEX_AFTER_POWER = 4;

/**
 * @typedef {object} PrescriptionBuilderRefs
 * @property {HTMLInputElement} rxTypeSingleInput
 * @property {HTMLInputElement} rxTypeProgressiveInput
 * @property {HTMLInputElement} rxTypeNoneInput
 * @property {HTMLElement} rxFields
 * @property {HTMLElement} wizardModal
 * @property {HTMLElement} materialBlock
 * @property {HTMLElement} progressiveBaseGroup
 * @property {HTMLElement} coatingGroup
 * @property {HTMLElement} lensPriceSummary
 * @property {HTMLInputElement} progressiveBasePropertyField
 * @property {HTMLInputElement} lensTypePropertyField
 * @property {HTMLInputElement} screensInput
 * @property {HTMLInputElement} sunInput
 * @property {HTMLInputElement} drivingInput
 * @property {HTMLElement} recommendationBanner
 * @property {HTMLElement} recommendationText
 * @property {HTMLInputElement} recommendationField
 * @property {HTMLElement} upsellCard
 * @property {HTMLInputElement} ageInput
 * @property {HTMLElement} ageSuggestion
 * @property {HTMLInputElement} addSuggestionField
 * @property {HTMLSelectElement} sphOdInput
 * @property {HTMLSelectElement} sphOiInput
 * @property {HTMLElement} materialNote
 * @property {HTMLInputElement} pdInput
 * @property {HTMLInputElement} materialPropertyField
 * @property {HTMLInputElement} cylOdInput
 * @property {HTMLInputElement} axisOdInput
 * @property {HTMLElement} axisOdHint
 * @property {HTMLInputElement} addOdInput
 * @property {HTMLElement} addOdHint
 * @property {HTMLInputElement} cylOiInput
 * @property {HTMLInputElement} axisOiInput
 * @property {HTMLElement} axisOiHint
 * @property {HTMLInputElement} addOiInput
 * @property {HTMLElement} addOiHint
 * @property {HTMLInputElement} confirmRxInput
 * @property {HTMLInputElement} entryMethodPhotoInput
 * @property {HTMLInputElement} entryMethodManualInput
 * @property {HTMLInputElement} entryMethodLaterInput
 * @property {HTMLElement} laterNote
 * @property {HTMLInputElement} laterField
 * @property {HTMLElement} manualRxGrid
 * @property {HTMLElement} photoUploadField
 * @property {HTMLElement} photoReceivedNote
*/

/**
 * Drives the eyewear prescription/lifestyle questionnaire on the product page:
 * toggles the Rx fields, computes soft lens-type recommendations from the
 * lifestyle answers, and suggests a reading-add power from age. Everything
 * computed here is written into hidden `properties[...]` inputs so it rides
 * along into the cart line item without touching the base add-to-cart form.
 * @extends Component<PrescriptionBuilderRefs>
 */
class PrescriptionBuilder extends Component {
  handleRxToggle() {
    const needsRx = !(this.refs.rxTypeNoneInput?.checked ?? false);
    const later = this.#wantsLater();
    if (this.refs.rxFields) this.refs.rxFields.hidden = !needsRx;
    // With "add my prescription later" nothing is typed now, so nothing is mandatory.
    if (this.refs.confirmRxInput) this.refs.confirmRxInput.required = needsRx && !later;
    // The interpupillary distance is needed for lenses with or without a typed prescription.
    if (this.refs.pdInput) this.refs.pdInput.required = needsRx && !later;
    // The "Receta" property only travels when the customer chose to send it later.
    if (this.refs.laterField) this.refs.laterField.disabled = !(needsRx && later);
    this.#syncPhotoRequired();
  }

  #wantsLater() {
    return this.refs.entryMethodLaterInput?.checked ?? false;
  }

  /**
   * "Solo montura" (the button outside the wizard) buys the frame with no lenses.
   * The wizard script flags it with data-frame-only. Picking "Sin receta" inside the
   * wizard is different: plano lenses, so treatments can still be chosen.
   */
  #isFrameOnly() {
    return (this.refs.rxTypeNoneInput?.checked ?? false) && this.dataset.frameOnly === 'true';
  }

  /** "Sin receta" chosen inside the wizard: lenses without prescription, treatments only. */
  #isPlano() {
    return (this.refs.rxTypeNoneInput?.checked ?? false) && !this.#isFrameOnly();
  }

  /**
   * When the customer chooses to send a photo of the prescription, the photo is
   * mandatory (a photo option with no photo leaves the order without any Rx).
   * It is not required for manual entry or when ordering the frame only.
   */
  #syncPhotoRequired() {
    const wantsPhoto = this.refs.entryMethodPhotoInput?.checked ?? false;
    const needsRx = !(this.refs.rxTypeNoneInput?.checked ?? false);
    const photoInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="file"][name$="[Foto de la receta]"]')
    );
    if (photoInput) photoInput.required = wantsPhoto && needsRx;
  }

  /**
   * Drives the cascading lens-selection UI based on which prescription type
   * is selected: Monofocal reveals the coating cards directly, Progresivo
   * reveals the progressive-base cards first and only then the coating
   * cards (once a base is picked, see handleLensSelectionChange), and
   * "Sin receta" skips lenses entirely and sends the order straight to
   * checkout with just the frame.
   */
  handleRxTypeChange() {
    const isFrameOnly = this.#isFrameOnly();
    const isProgressive = this.refs.rxTypeProgressiveInput?.checked ?? false;

    this.handleRxToggle();

    const { progressiveBaseGroup, coatingGroup, materialBlock } = this.refs;
    if (progressiveBaseGroup) progressiveBaseGroup.hidden = !isProgressive;
    if (coatingGroup) {
      coatingGroup.hidden = isProgressive ? !this.#getCheckedOption('progressive-base-option') : isFrameOnly;
    }
    // Plano lenses have no power, so thickness options make no sense.
    if (materialBlock) materialBlock.hidden = this.#isPlano();

    this.#syncAddRequired();
    this.#updateTreatmentVisibility();
    this.#updateMaterialOptions();
    this.handleLensSelectionChange();

    if (isFrameOnly) this.#closeWizardAndCheckout();
  }

  /**
   * A progressive lens is defined by its ADD power, so the ADD fields
   * become required once "Progresivo" is selected — but only in manual
   * entry mode, since a photo upload doesn't ask the customer to type
   * any values in.
   */
  #syncAddRequired() {
    const isProgressive = this.refs.rxTypeProgressiveInput?.checked ?? false;
    const wantsPhoto = this.refs.entryMethodPhotoInput?.checked ?? false;
    const required = isProgressive && !wantsPhoto && !this.#wantsLater();
    if (this.refs.addOdInput) this.refs.addOdInput.required = required;
    if (this.refs.addOiInput) this.refs.addOiInput.required = required;
    if (this.refs.addOdHint) this.refs.addOdHint.hidden = !required;
    if (this.refs.addOiHint) this.refs.addOiHint.hidden = !required;
  }

  /**
   * Toggles between the manual SPH/CYL/AXIS grid and a simple file upload
   * for customers who'd rather send a photo of their prescription than
   * type it in. Either way the PD field and confirmation checkbox stay
   * visible — PD isn't on a photo, and confirming still applies.
   */
  handleEntryMethodChange() {
    const wantsPhoto = this.refs.entryMethodPhotoInput?.checked ?? false;
    const later = this.#wantsLater();
    const { manualRxGrid, photoUploadField, photoReceivedNote, laterNote } = this.refs;

    if (manualRxGrid) manualRxGrid.hidden = wantsPhoto || later;
    if (photoReceivedNote) photoReceivedNote.hidden = !wantsPhoto;
    if (photoUploadField) photoUploadField.hidden = !wantsPhoto;
    if (laterNote) laterNote.hidden = !later;

    // Fields hidden via the `hidden` attribute are already exempt from
    // native form validation, but clear any stale required state on the
    // axis inputs so switching to photo mode never blocks submission.
    if (wantsPhoto || later) {
      if (this.refs.axisOdInput) this.refs.axisOdInput.required = false;
      if (this.refs.axisOiInput) this.refs.axisOiInput.required = false;
      if (this.refs.axisOdHint) this.refs.axisOdHint.hidden = true;
      if (this.refs.axisOiHint) this.refs.axisOiHint.hidden = true;
    } else {
      this.#syncAxisRequired(this.refs.cylOdInput, this.refs.axisOdInput, this.refs.axisOdHint);
      this.#syncAxisRequired(this.refs.cylOiInput, this.refs.axisOiInput, this.refs.axisOiHint);
    }
    this.handleRxToggle();
    this.#syncAddRequired();
    this.#syncPhotoRequired();
    this.#updateMaterialOptions();
  }

  /** SPH changed: re-evaluate which thickness options (reduction / high index) apply. */
  handlePowerInput() {
    this.#updateMaterialOptions();
  }

  /**
   * Cylinder (CYL) and Axis are optically inseparable: a cylinder value without
   * an axis is meaningless. Whenever either eye's CYL field has a value, that
   * eye's Axis field becomes required — enforced via native form validation
   * since these inputs ride along via the `form` attribute.
   * @param {Event} event
   */
  handleCylInput(event) {
    this.#updateMaterialOptions();
    const cylInput = /** @type {HTMLInputElement} */ (event.target);
    const isRightEye = cylInput === this.refs.cylOdInput;
    const axisInput = isRightEye ? this.refs.axisOdInput : this.refs.axisOiInput;
    const axisHint = isRightEye ? this.refs.axisOdHint : this.refs.axisOiHint;
    if (!axisInput) return;

    const cylHasValue = cylInput.value.trim() !== '';
    axisInput.required = cylHasValue;
    if (axisHint) axisHint.hidden = !cylHasValue;
  }

  handleAnswerChange() {
    this.#updateRecommendation();
  }

  handleAgeInput() {
    const age = Number(this.refs.ageInput?.value);
    const { ageSuggestion, addSuggestionField } = this.refs;
    if (!ageSuggestion || !addSuggestionField) return;

    const band = Number.isFinite(age) ? ADD_BY_AGE.find((b) => age >= b.min && age <= b.max) : undefined;

    if (band) {
      const addValue = `+${band.add.toFixed(2)}`;
      const template = ageSuggestion.dataset.template || '{add}';
      ageSuggestion.textContent = template.replace('{add}', addValue);
      ageSuggestion.hidden = false;
      addSuggestionField.value = addValue;
    } else {
      ageSuggestion.hidden = true;
      ageSuggestion.textContent = '';
      addSuggestionField.value = '';
    }
  }

  #productForm = null;
  /** @type {Set<string>} variant ids of lens products this component has put in the cart */
  #addedLensVariantIds = new Set();
  #lensCartSyncInProgress = false;
  #lensCartSyncQueued = false;

  connectedCallback() {
    super.connectedCallback();
    this.#productForm = this.dataset.productFormId ? document.getElementById(this.dataset.productFormId) : null;
    this.#productForm?.addEventListener('submit', this.#handleProductFormSubmit);
    this.handleRxTypeChange();
    this.#syncAxisRequired(this.refs.cylOdInput, this.refs.axisOdInput, this.refs.axisOdHint);
    this.#syncAxisRequired(this.refs.cylOiInput, this.refs.axisOiInput, this.refs.axisOiHint);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#productForm?.removeEventListener('submit', this.#handleProductFormSubmit);
  }

  /**
   * Updates the readable `properties[...]` fields and the on-page price
   * summary whenever the lens type or high-prescription surcharge selection
   * changes. Purely cosmetic/record-keeping — the actual cart total is fixed
   * by #handleProductFormSubmit adding the real lens product(s) as their own
   * cart line items.
   */
  handleLensSelectionChange() {
    const { progressiveOption, materialOption, treatmentOptions } = this.#currentLensSelection();
    const isProgressive = this.refs.rxTypeProgressiveInput?.checked ?? false;
    const needsRx = !(this.refs.rxTypeNoneInput?.checked ?? false);

    // Cascading reveal: once a progressive base is picked, show material + treatments.
    if (this.refs.coatingGroup && needsRx && isProgressive) {
      this.refs.coatingGroup.hidden = !progressiveOption;
    }

    if (this.refs.progressiveBasePropertyField) {
      this.refs.progressiveBasePropertyField.value = progressiveOption?.dataset.label || '';
    }
    if (this.refs.materialPropertyField) {
      this.refs.materialPropertyField.value = materialOption?.dataset.label || '';
    }
    if (this.refs.lensTypePropertyField) {
      this.refs.lensTypePropertyField.value = treatmentOptions.map((option) => option.dataset.label || '').filter(Boolean).join(', ');
    }

    const priceOf = (option) => Number(option?.dataset.price) || 0;
    const total =
      priceOf(progressiveOption) + priceOf(materialOption) + treatmentOptions.reduce((sum, option) => sum + priceOf(option), 0);

    if (this.refs.lensPriceSummary) {
      if (total > 0) {
        this.refs.lensPriceSummary.textContent = `+ ${'$'}${total.toFixed(2)} por el cristal seleccionado`;
        this.refs.lensPriceSummary.hidden = false;
      } else {
        this.refs.lensPriceSummary.textContent = '';
        this.refs.lensPriceSummary.hidden = true;
      }
    }
    this.#syncLensCartItems();
  }

  /**
   * Everything the customer has currently chosen in the lens step. Material
   * and treatments only count while their group is actually visible (for
   * progressives that means a base has been picked), so a hidden selection
   * never silently lands in the cart.
   */
  #currentLensSelection() {
    const lensesWanted = !this.#isFrameOnly();
    const isProgressive = this.refs.rxTypeProgressiveInput?.checked ?? false;

    const progressiveOption = lensesWanted && isProgressive ? this.#getCheckedOption('progressive-base-option') : null;
    const coatingsVisible = lensesWanted && (!isProgressive || !!progressiveOption);
    const materialOption = coatingsVisible && !this.#isPlano() ? this.#getCheckedOption('material-option') : null;
    const treatmentOptions = coatingsVisible ? this.#getCheckedOptions('treatment-option') : [];

    return { progressiveOption, materialOption, treatmentOptions, coatingsVisible };
  }

  /** @returns {Set<string>} variant ids that should be in the cart for the current selection */
  #desiredLensVariantIds() {
    const { progressiveOption, materialOption, treatmentOptions } = this.#currentLensSelection();
    const ids = new Set();
    [progressiveOption, materialOption, ...treatmentOptions].forEach((option) => {
      if (option?.value) ids.add(String(option.value));
    });
    return ids;
  }

  /**
   * Largest absolute SPH/CYL across both eyes (0 when nothing is entered).
   * @returns {number}
   */
  #maxAbsPower() {
    const { sphOdInput, sphOiInput, cylOdInput, cylOiInput } = this.refs;
    const values = [sphOdInput, sphOiInput, cylOdInput, cylOiInput]
      .map((input) => (input ? parseFloat(input.value) : NaN))
      .filter((value) => Number.isFinite(value))
      .map((value) => Math.abs(value));
    return values.length ? Math.max(...values) : 0;
  }

  /**
   * Thickness options are gated by the prescription: reduction appears above
   * ±2.00 and high-index from ±4.25 (SPH or CYL, either eye). With a photo
   * upload there are no typed values, so every option is shown and the
   * team confirms which one applies.
   */
  #updateMaterialOptions() {
    // Photo upload and "send it later" both mean no typed values: show every option.
    const wantsPhoto = (this.refs.entryMethodPhotoInput?.checked ?? false) || this.#wantsLater();
    const wantsLater = this.#wantsLater();
    const power = this.#maxAbsPower();
    let uncheckedOne = false;

    this.querySelectorAll('[data-role="material-option"]').forEach((input) => {
      const minPower = Number(/** @type {HTMLInputElement} */ (input).dataset.minPower) || 0;
      const allowed = minPower === 0 || wantsPhoto || power > minPower;
      const label = input.closest('label');
      if (label) label.hidden = !allowed;
      if (label) {
        const tier = label.dataset.tier;
        const recommended =
          !wantsPhoto &&
          ((tier === 'reduccion' && power > REDUCTION_MIN_POWER && power <= REDUCTION_MAX_RECOMMENDED) ||
            (tier === 'highindex' && power > HIGH_INDEX_AFTER_POWER));
        this.#setRecommendedPill(label, recommended);
      }
      if (!allowed && /** @type {HTMLInputElement} */ (input).checked) {
        /** @type {HTMLInputElement} */ (input).checked = false;
        uncheckedOne = true;
      }
    });

    if (uncheckedOne) {
      const standard = /** @type {HTMLInputElement | null} */ (
        this.querySelector('[data-role="material-option"][data-min-power="0"][value=""]')
      );
      if (standard) standard.checked = true;
      this.handleLensSelectionChange();
    }

    const { materialNote } = this.refs;
    if (materialNote) {
      let message = '';
      if (wantsLater) {
        message = 'Como enviarás tu receta después, te mostramos todas las opciones. Confirmaremos cuál corresponde a tu graduación.';
      } else if (wantsPhoto) {
        message = 'Como subirás una foto de tu receta, te mostramos todas las opciones. Confirmaremos cuál corresponde a tu graduación.';
      } else if (power === 0) {
        message = 'Al ingresar tu graduación te mostramos las opciones de reducción (desde ±2.00) y alto índice (desde ±4.25).';
      }
      materialNote.textContent = message;
      materialNote.hidden = !message;
    }
  }

  /** "Visión sencilla" only makes sense for monofocal lenses with a prescription. */
  #updateTreatmentVisibility() {
    const hideSimple = (this.refs.rxTypeProgressiveInput?.checked ?? false) || this.#isPlano();
    this.querySelectorAll('[data-role="treatment-option"][data-mono-only="true"]').forEach((input) => {
      const label = input.closest('label');
      if (label) label.hidden = hideSimple;
      if (hideSimple) /** @type {HTMLInputElement} */ (input).checked = false;
    });
  }

  /**
   * @param {string} role
   * @returns {HTMLInputElement | null}
   */
  #getCheckedOption(role) {
    return this.querySelector(`[data-role="${role}"]:checked`);
  }

  /**
   * @param {string} role
   * @returns {HTMLInputElement[]}
   */
  #getCheckedOptions(role) {
    return /** @type {HTMLInputElement[]} */ (Array.from(this.querySelectorAll(`[data-role="${role}"]:checked`)));
  }

  #closeWizardAndCheckout() {
    if (!this.#productForm) return;
    if (typeof this.#productForm.checkValidity === 'function' && !this.#productForm.checkValidity()) {
      this.#productForm.reportValidity();
      return;
    }

    const { wizardModal } = this.refs;
    if (wizardModal) {
      wizardModal.hidden = true;
      document.body.style.overflow = '';
    }
    if (typeof this.#productForm.requestSubmit === 'function') {
      this.#productForm.requestSubmit();
    } else {
      this.#productForm.submit();
    }
  }

/**
 * Safety net: syncs the cart on form submit in case a change event was
 * somehow missed (e.g. a value set programmatically without dispatching
 * change). Idempotent - only calls the network when the desired
 * selection differs from what is already in the cart.
 */
#handleProductFormSubmit = () => {
  this.#syncLensCartItems();
};

/**
 * The base add-to-cart form (product-form.js) only ever adds a single
 * variant: the frame. It has no concept of the separate Lentes product
 * catalog, so the selected lens type / surcharge can never be priced
 * into that request. Rather than modifying that shared, complex
 * add-to-cart flow, this keeps the selected lens product(s) in sync
 * with the cart as their own line item(s), adding/removing/replacing
 * on every selection change, well before the frame is ever added, so
 * by the time the customer clicks Agregar al carrito the lens is
 * already committed and the frame add refresh naturally shows the
 * correct combined total (avoids a race between two competing
 * cart-refresh/animation calls).
 */
async #syncLensCartItems() {
  if (this.#lensCartSyncInProgress) {
    this.#lensCartSyncQueued = true;
    return;
  }
  this.#lensCartSyncInProgress = true;

  try {
    const desired = this.#desiredLensVariantIds();
    const removals = [...this.#addedLensVariantIds].filter((id) => !desired.has(id));
    const additions = [...desired].filter((id) => !this.#addedLensVariantIds.has(id));

    if (removals.length === 0 && additions.length === 0) return;

    for (const variantId of removals) {
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: variantId, quantity: 0 }),
      });
      this.#addedLensVariantIds.delete(variantId);
    }

    if (additions.length > 0) {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: additions.map((id) => ({ id: Number(id), quantity: 1 })) }),
      });
      if (!response.ok) throw new Error("[prescription-builder] Lens add-to-cart failed: " + response.status);
      additions.forEach((id) => this.#addedLensVariantIds.add(id));
    }

    customElements.whenDefined("cart-items-component").then(() => {
      document.querySelector("cart-items-component")?.fetchCartData?.();
    });
  } catch (error) {
    console.warn("[prescription-builder] Failed to sync lens product(s) with cart:", error);
  } finally {
    this.#lensCartSyncInProgress = false;
    if (this.#lensCartSyncQueued) {
      this.#lensCartSyncQueued = false;
      this.#syncLensCartItems();
    }
  }
}


  /**
   * @param {HTMLInputElement} [cylInput]
   * @param {HTMLInputElement} [axisInput]
   * @param {HTMLElement} [axisHint]
   */
  #syncAxisRequired(cylInput, axisInput, axisHint) {
    if (!cylInput || !axisInput) return;
    const cylHasValue = cylInput.value.trim() !== '';
    axisInput.required = cylHasValue;
    if (axisHint) axisHint.hidden = !cylHasValue;
  }

  #updateRecommendation() {
    const { screensInput, sunInput, drivingInput, recommendationBanner, recommendationText, recommendationField, upsellCard } =
      this.refs;

    const screens = screensInput?.checked ?? false;
    const sun = sunInput?.checked ?? false;
    const driving = drivingInput?.checked ?? false;

    /** @type {string[]} */
    const messages = [];
    /** @type {string[]} */
    const tags = [];

    if (driving) {
      messages.push(this.dataset.msgDriving || '');
      tags.push('Transitions XTRActive');
    } else if (sun) {
      messages.push(this.dataset.msgSun || '');
      tags.push('Transitions');
    }

    if (screens) {
      messages.push(this.dataset.msgScreens || '');
      tags.push('Blue Block');
    }

    const hasRecommendation = messages.length > 0;

    if (recommendationBanner) recommendationBanner.hidden = !hasRecommendation;
    if (recommendationText) recommendationText.textContent = messages.filter(Boolean).join(' ');
    if (recommendationField) recommendationField.value = tags.join(', ');

    if (upsellCard) {
      const hasNightProduct = upsellCard.dataset.hasNightProduct === 'true';
      upsellCard.hidden = !(screens && hasNightProduct);
    }

    /** @type {string[]} product titles of the treatments to flag as recommended */
    const recommendedTreatments = [];
    if (driving) recommendedTreatments.push('Fotosensibles', 'Polarizado');
    else if (sun) recommendedTreatments.push('Fotosensibles');
    if (screens) recommendedTreatments.push('Blue blocker');
    this.#markRecommendedTreatments(recommendedTreatments);
  }

  /**
   * Adds a "Recomendado para ti" pill to the treatment cards that match the
   * lifestyle answers, and removes it from the ones that no longer do.
   * @param {string[]} recommendedTitles
   */
  #markRecommendedTreatments(recommendedTitles) {
    this.querySelectorAll('[data-role="treatment-option"]').forEach((input) => {
      const label = input.closest('label');
      if (!label) return;
      const isRecommended = recommendedTitles.includes(/** @type {HTMLInputElement} */ (input).dataset.label || '');
      this.#setRecommendedPill(label, isRecommended);
    });
  }

  /**
   * Shows or removes the "Recomendado para ti" pill on an option card.
   * @param {HTMLElement} label
   * @param {boolean} isRecommended
   */
  #setRecommendedPill(label, isRecommended) {
    const title = label.querySelector('.prescription-builder__card-title');
    if (!title) return;

    let pill = title.querySelector('.prescription-builder__recommended-pill');
    if (isRecommended && !pill) {
      pill = document.createElement('span');
      pill.className = 'prescription-builder__recommended-pill';
      pill.textContent = 'Recomendado para ti';
      /** @type {HTMLElement} */ (pill).style.cssText =
        'display:inline-block;margin-inline-start:8px;padding:2px 8px;border-radius:999px;' +
        'background:var(--color-logo, var(--color-foreground));color:var(--color-background);' +
        'font-size:0.7em;font-weight:600;letter-spacing:0.02em;text-transform:none;white-space:nowrap;vertical-align:middle;';
      title.appendChild(pill);
    } else if (!isRecommended && pill) {
      pill.remove();
    }
    label.toggleAttribute('data-recommended', isRecommended);
  }
}

if (!customElements.get('prescription-builder-component')) {
  customElements.define('prescription-builder-component', PrescriptionBuilder);
}
